import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useParams } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { List } from 'immutable';
import moment from 'moment';

import { clearMissedCallState } from 'flow/actions/app-actions';
import { fetchPersonalAndSharedContacts } from 'flow/actions/contact-actions';
import {
    fetchCallRecordsForExtension,
    fetchExtensions,
    fetchVoiceMessages,
} from 'flow/actions/extension-actions';
import { getFavorites } from 'flow/actions/favorites-actions';
import CallHistorySkeletonV2 from 'flow/components/call-history-v2/CallHistorySkeletonV2';
import Divider from 'flow/components/ui-components/Divider';
import ScrollablePillTabs from 'flow/components/ui-components/ScrollablePillTabs';
import { TabV2Props } from 'flow/components/pbx/v2/misc/TabsV2';
import ButtonDeleteAllVoicemails from 'flow/components/call-history-v2/ButtonDeleteAllVoicemails';
import useAppContext from 'flow/hooks/useAppContext';
import { selectOwnExtensionKey } from 'flow/selectors/ExtensionSelectors';
import {
    getEarliestConnectedCallRecordStillActiveTimestampInMilliseconds,
    getFilteredCallRecordsHistory,
    getFilteredVoicemessagesFetchedFromVoicemailsApi,
    selectOwnActiveCallsCount,
} from 'flow/selectors/call-selectors';
import { CallRecord } from 'flow/types/CallRecord';
import { CallRecordKey } from 'flow/types/EntityKeys';
import { CallParams } from 'flow/types/CallParams';
import { TypedMap } from 'flow/types/TypedMap';
import { ExtensionVoiceMessage } from 'flow/types/VoiceMessage';
import { groupListBy, isEmptyNullOrUndefined } from 'flow/utils';
import { getCallRecordDisplayTimestamp } from 'flow/utils/call-record-utils';
import { callRecordIsMissed } from 'flow/utils/call-records-utils';
import { calcTotalUnreadVoiceMessages } from 'flow/utils/extension-utils';
import DateGroupedCallRecordList from './DateGroupedCallRecordList';
import { type DateGroups } from './types';
import EmptyBubble from 'flow/components/nav/empty-states/EmptyBubble';
import NoMatchOnSearch from 'flow/components/nav/empty-states/NoMatchOnSearch';

const TAB = {
    ALL: 'ALL',
    MISSED: 'MISSED',
    VOICEMAIL: 'VOICEMAIL',
};

const fetchCallRecordsFlags = [
    'contact',
    'recordings',
    'voicemessages',
    'apps',
];

const SCROLL_BOTTOM_THRESHOLD = 100;

const CallHistoryListV2 = () => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);
    const { callRecordKey: selectedCallRecordKey } = useParams<CallParams>();
    const [selectedTab, setSelectedTab] = useState(TAB.ALL);
    const [displayLoading, setDisplayLoading] = useState(false);
    const isLoadingMore = useRef(false);

    const appIsInFocus = useAppSelector(
        (state) => state.app.get('focus') && state.app.get('visible'),
    );

    const timestampToFetchBeforeOnScrollToBottom = useAppSelector((state) =>
        state.callRecordsData.getIn([
            'sortingData',
            'extensions',
            ownExtensionKey,
            'all',
            'nextBefore',
        ]),
    );

    const callFilter = useAppSelector((state) =>
        state.extensions.get('callListFilter', ''),
    );

    const ownActiveCallsCount = useAppSelector(selectOwnActiveCallsCount);

    const callRecords: TypedMap<Record<CallRecordKey, TypedMap<CallRecord>>> =
        useAppSelector(getFilteredCallRecordsHistory);

    const voiceMessages = useAppSelector(
        getFilteredVoicemessagesFetchedFromVoicemailsApi,
    );

    const timestampToFetchAfterOnCallUpdate = useAppSelector(
        getEarliestConnectedCallRecordStillActiveTimestampInMilliseconds,
    );

    const prevOwnActiveCallsCount = useRef(ownActiveCallsCount);

    useEffect(() => {
        setDisplayLoading(true);
        dispatch(
            fetchCallRecordsForExtension(
                ownExtensionKey,
                fetchCallRecordsFlags,
            ),
        ).finally(() => setDisplayLoading(false));

        dispatch(fetchVoiceMessages(ownExtensionKey, 'contact'));
        dispatch(fetchExtensions('live', 'contact'));
        dispatch(getFavorites());
        dispatch(fetchPersonalAndSharedContacts());
    }, [dispatch, ownExtensionKey]);

    useEffect(() => {
        if (prevOwnActiveCallsCount.current > ownActiveCallsCount) {
            dispatch(
                fetchCallRecordsForExtension(
                    ownExtensionKey,
                    fetchCallRecordsFlags,
                    timestampToFetchAfterOnCallUpdate,
                    null,
                ),
            );
        }

        prevOwnActiveCallsCount.current = ownActiveCallsCount;
    }, [
        dispatch,
        ownExtensionKey,
        timestampToFetchAfterOnCallUpdate,
        ownActiveCallsCount,
    ]);

    useEffect(() => {
        if (appIsInFocus) {
            dispatch(clearMissedCallState());
        }
    }, [dispatch, appIsInFocus]);

    const handleScroll = useCallback(
        (e: React.UIEvent<HTMLDivElement>) => {
            if (
                isLoadingMore.current ||
                !timestampToFetchBeforeOnScrollToBottom
            )
                return;
            const el = e.currentTarget;
            if (
                el.scrollHeight - el.scrollTop - el.clientHeight <
                SCROLL_BOTTOM_THRESHOLD
            ) {
                isLoadingMore.current = true;
                dispatch(
                    fetchCallRecordsForExtension(
                        ownExtensionKey,
                        fetchCallRecordsFlags,
                        null,
                        timestampToFetchBeforeOnScrollToBottom,
                    ),
                ).finally(() => {
                    isLoadingMore.current = false;
                });
            }
        },
        [dispatch, ownExtensionKey, timestampToFetchBeforeOnScrollToBottom],
    );

    const onClickTab = (tab: TabV2Props) => setSelectedTab(String(tab.key));

    const getEmptyText = () => {
        switch (selectedTab) {
            case TAB.ALL:
                return (
                    <EmptyBubble
                        title={i18next.t('History.Empty.All.Description')}
                        imageId={'callHistory'}
                    />
                );
            case TAB.MISSED:
                return (
                    <EmptyBubble
                        title={i18next.t('History.Empty.Calls.Description')}
                        imageId={'missedCalls'}
                    />
                );
            case TAB.VOICEMAIL:
                return (
                    <EmptyBubble
                        title={i18next.t('History.Empty.Voicemail.Description')}
                        imageId={'voicemail'}
                    />
                );
            default:
                return null;
        }
    };

    const tabIsVoicemail = selectedTab === TAB.VOICEMAIL;

    const dateGroups: DateGroups | null = useMemo(() => {
        const sortDateGroupsByKey = <T,>(
            groups: TypedMap<Record<string, T>>,
        ): DateGroups =>
            groups.sortBy(
                (_value, key) => key,
                (dateA, dateB) => (dateA < dateB ? 1 : -1),
            ) as unknown as DateGroups;

        if (tabIsVoicemail) {
            const grouped = groupListBy(
                voiceMessages,
                (item: TypedMap<ExtensionVoiceMessage>) =>
                    item.get('receivedDate').split('T')[0],
            );
            return sortDateGroupsByKey(grouped);
        }

        if (!callRecords) return null;

        let filtered = callRecords;
        if (selectedTab === TAB.MISSED) {
            filtered = callRecords.filter((callRecord) =>
                callRecordIsMissed(callRecord),
            );
        } else if (selectedTab !== TAB.ALL) {
            filtered = callRecords.filter((callRecord) =>
                callRecord.has('voiceMessage'),
            );
        }

        const grouped = groupListBy(filtered, (item: TypedMap<CallRecord>) =>
            moment(getCallRecordDisplayTimestamp(item, 'personal')).format(
                'YYYY-MM-DD',
            ),
        ).map((group: List<TypedMap<CallRecord>>) =>
            group.sort((a, b) => {
                const aTime = getCallRecordDisplayTimestamp(a, 'personal');
                const bTime = getCallRecordDisplayTimestamp(b, 'personal');
                if (aTime === bTime) return 0;
                return (aTime ?? '') < (bTime ?? '') ? 1 : -1;
            }),
        );
        return sortDateGroupsByKey(grouped);
    }, [callRecords, voiceMessages, selectedTab, tabIsVoicemail]);

    const recordCount =
        dateGroups?.reduce(
            (sum: number, group: { size: number }) => sum + group.size,
            0,
        ) || 0;

    const isEmpty = recordCount === 0 && isEmptyNullOrUndefined(callFilter);

    const noSearchMatch =
        !isEmptyNullOrUndefined(callFilter) && recordCount === 0;

    const tabs = [
        { key: TAB.ALL, label: i18next.t('call.all') },
        { key: TAB.MISSED, label: i18next.t('call.missed_calls') },
        {
            key: TAB.VOICEMAIL,
            label: i18next.t('call.voicemail'),
            badgeCounter: calcTotalUnreadVoiceMessages(voiceMessages),
        },
    ];

    return (
        <div className="flex min-h-0 flex-1 flex-col">
            {/* The filter shows at once, so the arrow keys select. */}
            <ScrollablePillTabs
                tabs={tabs}
                selectedKey={selectedTab}
                onClick={onClickTab}
                stretch={false}
                activation="automatic"
            />

            {displayLoading && isEmpty && <CallHistorySkeletonV2 />}

            {isEmpty && getEmptyText()}

            {!isEmpty && noSearchMatch && (
                <NoMatchOnSearch>
                    {i18next.t('Call.SearchEmpty.Description', {
                        filterText: callFilter,
                    })}
                </NoMatchOnSearch>
            )}

            {!isEmpty && !noSearchMatch && (
                <div
                    className="scrollbar scrollbar-gutter-stable min-h-0 flex-1 ps-3 pe-1.5 pb-3"
                    data-test-id="call-history-list"
                    data-context-menu="false"
                    onScroll={handleScroll}
                >
                    <DateGroupedCallRecordList
                        dateGroups={dateGroups}
                        selectedCallRecordKey={selectedCallRecordKey}
                        bgClassName="bg-gray-100"
                    />
                </div>
            )}

            {selectedTab === TAB.VOICEMAIL && !isEmpty && (
                <>
                    <Divider className="my-0 bg-gray-300" />
                    <ButtonDeleteAllVoicemails />
                </>
            )}
        </div>
    );
};

export default CallHistoryListV2;
